import { useMemo, useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Transaction } from "@/types/database";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip } from "recharts";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, DollarSign } from "lucide-react";
import { MonthYearPicker } from "@/components/common/MonthYearPicker";
import { getCategoryIcon } from "@/constants/categoryIcons";

interface TransactionWithCategory extends Transaction {
  categoria?: { nome?: string; cor?: string; icone?: string } | null;
}

interface PendingExpensesReportProps {
  transactions: TransactionWithCategory[];
  selectedDate?: Date;
}

export function PendingExpensesReport({ transactions, selectedDate }: PendingExpensesReportProps) {
  // Função para formatar a descrição removendo a numeração de parcela
  const formatDescription = (transaction: TransactionWithCategory) => {
    if (!transaction.descricao) return '';
    // Remove o padrão (X/Y) da descrição para evitar duplicação com o badge
    return transaction.descricao.replace(/\s*\(\d+\/\d+\)\s*$/, '');
  };
  
  // Função para verificar se é uma transação parcelada
  const isInstallmentTransaction = (transaction: TransactionWithCategory) => {
    // Verifica se a descrição contém um padrão de parcela (X/Y)
    return transaction.descricao && transaction.descricao.match(/\(\d+\/\d+\)/);
  };
  
  // Função para exibir o badge de parcela
  const renderInstallmentBadge = (transaction: TransactionWithCategory) => {
    if (!isInstallmentTransaction(transaction)) return null;
    
    // Extrai as informações de parcela da descrição
    const match = transaction.descricao.match(/\((\d+)\/(\d+)\)/);
    if (!match) return null;
    
    const numeroParcela = match[1];
    const totalParcelas = match[2];
    
    return (
      <Badge variant="outline" className="ml-2 text-xs">
        {numeroParcela}/{totalParcelas}
      </Badge>
    );
  };

  const isMobile = useIsMobile();
  
  // Helper function to find the latest month with pending expenses
  const getLatestMonthWithPendingExpenses = (transactions: TransactionWithCategory[]) => {
    if (!transactions || transactions.length === 0) {
      return { month: new Date().getMonth(), year: new Date().getFullYear() };
    }
    
    const realTransactions = transactions.filter(t => {
      try {
        return t && !String(t.id).startsWith('virtual-') && t.data;
      } catch (e) {
        return false;
      }
    }) as TransactionWithCategory[];
    
    const pendingExpenses = realTransactions.filter(t => {
      try {
        return t && t.tipo === 'saida' && !t.foi_paga && t.data;
      } catch (e) {
        return false;
      }
    });
    
    if (pendingExpenses.length === 0) {
      // Try to find any expense (paid or unpaid) for a more relevant starting point
      const allExpenses = realTransactions.filter(t => {
        try {
          return t && t.tipo === 'saida' && t.data;
        } catch (e) {
          return false;
        }
      });
      
      if (allExpenses.length > 0) {
        try {
          const validDates = allExpenses.map(t => {
            try {
              const date = new Date(t.data);
              return isNaN(date.getTime()) ? null : date.getTime();
            } catch (e) {
              return null;
            }
          }).filter(time => time !== null);
          
          if (validDates.length > 0) {
            const latestExpense = new Date(Math.max(...validDates));
            return { 
              month: latestExpense.getMonth(), 
              year: latestExpense.getFullYear() 
            };
          }
        } catch (e) {
          // Fallback
        }
      }
      
      return { month: new Date().getMonth(), year: new Date().getFullYear() };
    }
    
    try {
      const validDates = pendingExpenses.map(t => {
        try {
          const date = new Date(t.data);
          return isNaN(date.getTime()) ? null : date.getTime();
        } catch (e) {
          return null;
        }
      }).filter(time => time !== null);
      
      if (validDates.length === 0) {
        return { month: new Date().getMonth(), year: new Date().getFullYear() };
      }
      
      const latestPending = new Date(Math.max(...validDates));
      return { 
        month: latestPending.getMonth(), 
        year: latestPending.getFullYear() 
      };
    } catch (e) {
      return { month: new Date().getMonth(), year: new Date().getFullYear() };
    }
  };

  const [pendingIds, setPendingIds] = useState<string[]>(() => []);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [accountMap, setAccountMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const { data, error } = await supabase.from('contas').select('id, tipo');
        if (data && !error) {
          const map: Record<string, string> = {};
          data.forEach(acc => {
            map[acc.id] = acc.tipo;
          });
          setAccountMap(map);
        }
      } catch (err) {
        console.error('Erro ao buscar contas:', err);
      }
    };
    fetchAccounts();
  }, []);

  // Update selected month/year when transactions are loaded or selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      // Use the selectedDate from props when available
      setSelectedMonth(selectedDate.getMonth());
      setSelectedYear(selectedDate.getFullYear());
    } else if (transactions && transactions.length > 0) {
      // Fall back to latest month with pending expenses when no selectedDate
      const latestPeriod = getLatestMonthWithPendingExpenses(transactions);
      setSelectedMonth(latestPeriod.month);
      setSelectedYear(latestPeriod.year);
    }
  }, [transactions, selectedDate]);

  // Helper: format currency
  const formatCurrency = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  // Helper: safe date formatting
  const formatSafeDate = (dateString: string | null | undefined) => {
    if (!dateString || dateString === '' || dateString === 'null' || dateString === 'undefined') {
      return 'Sem data';
    }
    try {
      // Handle different date formats
      let date: Date;
      if (dateString.includes('T') || dateString.includes('+')) {
        // For ISO strings with timezone, extract just the date part to avoid timezone conversion
        const dateOnly = dateString.split('T')[0];
        const [year, month, day] = dateOnly.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        // For date-only strings, parse as local date
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day);
      }
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date found:', dateString);
        return 'Data inválida';
      }
      
      return format(date, 'dd/MM/yyyy', { locale: ptBR });
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return 'Erro na data';
    }
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(prev => prev - 1);
    } else {
      setSelectedMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(prev => prev + 1);
    } else {
      setSelectedMonth(prev => prev + 1);
    }
  };

  const realTransactions = useMemo(() => {
    return (transactions || []).filter(t => !String(t.id).startsWith('virtual-')) as TransactionWithCategory[];
  }, [transactions]);

  const pendingExpenses = useMemo(() => {
    return realTransactions.filter(t => {
      if (!t.data) return false;
      const d = new Date(t.data);
      if (isNaN(d.getTime())) return false;
      const isSameMonth = d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
      const isCreditCard = t.conta_id && accountMap[t.conta_id] === 'cartao_credito';
      return t.tipo === 'saida' && !t.foi_paga && isSameMonth && !isCreditCard;
    });
  }, [realTransactions, selectedMonth, selectedYear, accountMap]);

  // Local override for ones marked paid in this view
  const visibleExpenses = useMemo(() => {
    return pendingExpenses
      .filter(t => !pendingIds.includes(t.id))
      .sort((a, b) => {
        const dateA = new Date(a.data);
        const dateB = new Date(b.data);

        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;

        return dateA.getTime() - dateB.getTime();
      });
  }, [pendingExpenses, pendingIds]);

  const totalPendingAmount = useMemo(() => {
    return visibleExpenses.reduce((sum, t) => sum + Number(t.valor || 0), 0);
  }, [visibleExpenses]);

  const totalsByCategory = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of visibleExpenses) {
      const name = t.categoria?.nome || 'Sem categoria';
      map.set(name, (map.get(name) || 0) + Number(t.valor || 0));
    }
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [visibleExpenses]);

  const chartData = totalsByCategory;


  const handleMarkAsPaid = async (id: string) => {
    try {
      const { error } = await supabase
        .from('transacoes')
        .update({ 
          foi_paga: true,
          data_pagamento: format(new Date(), 'yyyy-MM-dd')
        })
        .eq('id', id);
      if (error) throw error;

      setPendingIds(prev => [...prev, id]);
      toast.success('Despesa marcada como paga - O relatório foi atualizado.');
    } catch (err) {
      console.error('Erro ao marcar como paga:', err);
      toast.error('Não foi possível atualizar a despesa.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium text-muted-foreground">
                Total Despesas Pendentes
              </CardTitle>
          <div className="p-2 rounded-md bg-expense text-expense-foreground">
            <DollarSign className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-base font-bold text-expense">{formatCurrency(totalPendingAmount)}</div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {visibleExpenses.length} despesa{visibleExpenses.length !== 1 ? 's' : ''} pendente{visibleExpenses.length !== 1 ? 's' : ''}
          </p>
        </CardContent>
      </Card>

      {/* Month Navigation */}
      <MonthYearPicker
        selectedDate={new Date(selectedYear, selectedMonth, 1)}
        onDateChange={(date) => {
          setSelectedMonth(date.getMonth());
          setSelectedYear(date.getFullYear());
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Despesas Pendentes por Categoria</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {visibleExpenses.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Sem despesas pendentes para o período selecionado.</p>
          ) : isMobile ? (
            // Mobile: Horizontal bar chart for better category name visibility
            <div className="space-y-3">
              <div className="w-full bg-card rounded-2xl shadow-sm border p-2">
                <div className="h-[320px] sm:h-[350px]">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={(showAllCategories ? chartData : chartData.slice(0, 8)).sort((a, b) => b.value - a.value)}
                        layout="vertical"
                        margin={{ left: 5, right: 30, top: 15, bottom: 15 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          type="number"
                          domain={[0, 'dataMax']}
                          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                          tickFormatter={(v) => {
                            const value = Number(v);
                            return value >= 1000 
                              ? `R$ ${(value / 1000).toFixed(1)}k`
                              : `R$ ${value.toFixed(0)}`;
                          }}
                        />
                        <YAxis 
                          type="category" 
                          dataKey="name"
                          width={isMobile ? 120 : 85}
                          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                          tickFormatter={(name) => name.length > 15 ? name.substring(0, 15) + '...' : name}
                        />
                        <ReTooltip 
                          formatter={(v: number) => [formatCurrency(Number(v)), "Valor"]}
                          labelFormatter={(name) => name}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--background))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                        <Bar 
                          dataKey="value" 
                          fill="hsl(var(--destructive))" 
                          radius={[0, 6, 6, 0]} 
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      Nenhum dado disponível
                    </div>
                  )}
                </div>
                
                {chartData.length > 8 && (
                  <div className="flex justify-center mt-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAllCategories(!showAllCategories)}
                      className="text-sm"
                    >
                      {showAllCategories 
                        ? `Mostrar menos` 
                        : `Ver todas (${chartData.length})`}
                    </Button>
                  </div>
                )}
                
                {!showAllCategories && chartData.length > 8 && (
                  <p className="text-xs text-muted-foreground text-center mt-1">
                    Top 8 de {chartData.length} categorias
                  </p>
                )}
              </div>
            </div>
          ) : (
            // Desktop: Vertical bar chart
            <div className="w-full h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ left: 20, right: 20, top: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(name) => name.length > 15 ? name.substring(0, 15) + '...' : name}
                  />
                  <YAxis 
                    width={90}
                    domain={[0, 'dataMax']}
                    tickFormatter={(v) => {
                      const value = Number(v);
                      return value >= 1000 
                        ? `R$ ${(value / 1000).toFixed(1)}k`
                        : `R$ ${value.toFixed(0)}`;
                    }}
                  />
                  <ReTooltip 
                    formatter={(v: number) => [formatCurrency(Number(v)), "Valor"]}
                    labelFormatter={(name) => name}
                  />
                  <Bar dataKey="value" fill="#ef4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lista de Despesas Pendentes</CardTitle>
        </CardHeader>
        <CardContent>
          {visibleExpenses.length === 0 ? (
            <div className="text-center text-muted-foreground py-6">
              Nenhuma despesa pendente.
            </div>
          ) : (
            <div className={isMobile ? "h-[400px] overflow-y-auto" : "h-[600px] overflow-y-auto"}>
              {isMobile ? (
                // Mobile: Card-based layout
                <div className="space-y-3">
                  {visibleExpenses.map((t) => (
                    <div key={t.id} className="border rounded-lg p-4 hover:bg-muted/30 transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate flex items-center">
                            <span>{formatDescription(t)}</span>
                            {renderInstallmentBadge(t)}
                          </div>
                          <div className="text-base font-bold text-expense mt-1">
                            {formatCurrency(Number(t.valor || 0))}
                          </div>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="default" size="sm" className="ml-3 shrink-0">
                              Pagar
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmar Pagamento</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja confirmar o pagamento desta despesa?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleMarkAsPaid(t.id)}>
                                Confirmar Pagamento
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 items-center text-sm">
                        {t.categoria ? (
                          <Badge variant="outline" className="gap-1">
                            {(() => {
                              const Icon = getCategoryIcon(t.categoria?.icone);
                              return <Icon className="w-3 h-3" style={{ color: t.categoria?.cor }} />;
                            })()}
                            {t.categoria.nome}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            Sem categoria
                          </Badge>
                        )}
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>{formatSafeDate(t.data)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop: Table layout
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%]">Descrição</TableHead>
                      <TableHead className="w-[20%]">Categoria</TableHead>
                      <TableHead className="w-[15%]">Valor</TableHead>
                      <TableHead className="w-[15%]">Vencimento</TableHead>
                      <TableHead className="w-[20%]">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleExpenses.map((t) => (
                      <TableRow key={t.id} className="hover:bg-muted/30">
                        <TableCell className="font-medium">
                          <div className="truncate flex items-center" title={formatDescription(t)}>
                            <span>{formatDescription(t)}</span>
                            {renderInstallmentBadge(t)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {t.categoria ? (
                            <Badge variant="outline" className="gap-1">
                              {(() => {
                                const Icon = getCategoryIcon(t.categoria!.icone);
                                return <Icon className="w-3 h-3" style={{ color: t.categoria!.cor }} />;
                              })()}
                              {t.categoria.nome}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">Sem categoria</span>
                          )}
                        </TableCell>
                        <TableCell className="font-semibold text-expense">
                          {formatCurrency(Number(t.valor || 0))}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{formatSafeDate(t.data)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="default" size="sm" className="w-full">
                                Marcar como paga
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar Pagamento</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja confirmar o pagamento desta despesa?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleMarkAsPaid(t.id)}>
                                  Confirmar Pagamento
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
